/**
 * Local Database - Ledger Operations
 *
 * CRUD operations for ledgers.
 */

import { initializeLocalDb } from './init'
import {
  localStore,
  isElectron,
  getElectronWindow,
  saveToLocalStorage,
} from './store'

import type { LocalLedger } from './types'

/**
 * Get all ledgers from local database
 */
export async function getLocalLedgers(orgId: string): Promise<LocalLedger[]> {
  if (isElectron) {
    return getElectronWindow().electronAPI.db.getLedgers(orgId)
  }

  // Initialize if needed
  /* v8 ignore next 3 */
  if (!localStore.initialized) {
    await initializeLocalDb(orgId)
  }

  return Array.from(localStore.ledgers.values())
    .filter(l => l.orgId === orgId)
    .sort((a, b) => a.code.localeCompare(b.code))
}

/**
 * Get ledger by ID
 */
export async function getLocalLedger(orgId: string, ledgerId: string): Promise<LocalLedger | null> {
  if (isElectron) {
    return getElectronWindow().electronAPI.db.getLedger(orgId, ledgerId)
  }

  const ledger = localStore.ledgers.get(ledgerId)
  return ledger?.orgId === orgId ? ledger : null
}

/**
 * Create a new ledger
 */
export async function createLocalLedger(
  orgId: string,
  data: Omit<LocalLedger, 'id' | 'orgId' | 'createdAt' | 'updatedAt'>
): Promise<LocalLedger> {
  const id = crypto.randomUUID()
  const now = new Date().toISOString()

  const ledger: LocalLedger = {
    ...data,
    id,
    orgId,
    createdAt: now,
    updatedAt: now,
  }

  if (isElectron) {
    return getElectronWindow().electronAPI.db.createLedger(ledger)
  }

  localStore.ledgers.set(id, ledger)

  // Set as default ledger if it's the first one or if it's a primary ledger
  if (!localStore.defaultLedgerId || data.ledgerType === 'primary') {
    localStore.defaultLedgerId = id
  }

  saveToLocalStorage()
  return ledger
}

/**
 * Update a ledger
 */
export async function updateLocalLedger(
  orgId: string,
  ledgerId: string,
  data: Partial<LocalLedger>
): Promise<LocalLedger> {
  const existing = await getLocalLedger(orgId, ledgerId)
  if (!existing) {throw new Error('Ledger not found')}

  const updated: LocalLedger = {
    ...existing,
    ...data,
    id: existing.id,
    orgId: existing.orgId,
    createdAt: existing.createdAt,
    updatedAt: new Date().toISOString(),
  }

  if (isElectron) {
    return getElectronWindow().electronAPI.db.updateLedger(updated)
  }

  localStore.ledgers.set(ledgerId, updated)
  saveToLocalStorage()
  return updated
}
